/**
 * webVitals.ts · Real-User Core Web Vitals Tracking
 *
 * Misst die echten Ladewerte ECHTER Besucher (LCP, CLS, INP, FCP, TTFB) pro
 * Seite und pro A/B-Variante und schickt sie fire-and-forget an die Edge
 * Function `track-web-vitals`. Damit lässt sich BELEGEN, dass die Landingpages
 * technisch schnell sind — Feld-Daten, nicht Labor.
 *
 * GRACEFUL DEGRADATION ist Regel #1 (wie in experiments.ts): Wenn irgendein
 * Schritt scheitert (web-vitals fehlt, experiments.ts wirft, Netzwerk weg,
 * Storage blockiert), MUSS die Seite trotzdem perfekt laufen. Alles hier ist in
 * try/catch gekapselt, fire-and-forget, und wirft NIE.
 *
 * web-vitals ruft jeden Handler standardmäßig GENAU EINMAL pro Page-Load auf
 * (final value) → keine Doppel-Meldungen nötig. Zusätzlich ein In-Memory-Guard
 * pro Metrik als Sicherheitsnetz.
 *
 * VORAB GERENDERTE SEITEN (Speculation Rules, Paket 1d): web-vitals 4.2.4 kann das
 * bereits von selbst. FCP/LCP/TTFB werden über `whenActivated` erst nach dem
 * `prerenderingchange` gemessen und relativ zu `activationStart` gerechnet, CLS/INP
 * finalisieren erst nach Sichtbarwerden (der Visibility-Watcher steht während des
 * Prerenders auf „nie sichtbar“), und `navigationType` heißt dann `prerender`. Genau
 * diese Werte (LCP p75 422–876 ms) sind der Beleg für den Nutzen der Regeln. Unser
 * `report` wartet trotzdem zusätzlich auf die Aktivierung: kostet nichts, und die
 * Sende-Logik hängt so nicht an einem Bibliotheks-Detail.
 */

import { supabase } from "@/integrations/supabase/client";
import { bisAktiviert } from "@/lib/prerendering";
import { getPlatform } from "@/lib/platform";

const AB_EXPERIMENT_KEY = "gassi_hero";

// Sicherheitsnetz gegen Doppel-Firings innerhalb einer Page-Session.
const reported = new Set<string>();

// Pfad beim SEITEN-EINSTIEG. CLS/INP melden erst beim Verlassen — in der SPA
// wäre location.pathname dann die ZULETZT besuchte Seite, nicht die Navigation,
// zu der die Metrik gehört (so bekam /schnuppertag die Session-Shifts des
// ganzen Funnels zugebucht). Alle 5 CWV beschreiben die echte Navigation →
// landing_path ist der korrekte Bezug.
let landingPath: string | null = null;

/**
 * Kompakte Attribution je Metrik (web-vitals Attribution-Build): benennt bei
 * CLS das größte verschobene Element, bei LCP das Element, bei INP das
 * Interaktionsziel. Best effort — bei jedem Fehler null.
 */
function compactAttribution(name: string, attribution: unknown): Record<string, unknown> | null {
  try {
    const a = attribution as Record<string, unknown> | undefined;
    if (!a) return null;
    if (name === "CLS") {
      return {
        target: a.largestShiftTarget ?? null,
        value: (a.largestShiftEntry as { value?: number } | undefined)?.value ?? null,
        loadState: a.loadState ?? null,
      };
    }
    if (name === "LCP") {
      return { target: a.element ?? null, url: a.url ?? null };
    }
    if (name === "INP") {
      return { target: a.interactionTarget ?? null, type: a.interactionType ?? null };
    }
    return null;
  } catch {
    return null;
  }
}

function plattform(): string {
  try {
    return getPlatform();
  } catch {
    return "web";
  }
}

/**
 * Best-effort Variant/Visitor aus der A/B-Harness. Beide Importe könnten
 * theoretisch fehlschlagen (Bundle-Split-Fehler) → still auf null degradieren.
 */
async function getAbContext(): Promise<{
  visitorId: string | null;
  variant: string | null;
}> {
  try {
    const mod = await import("@/lib/experiments");
    let visitorId: string | null = null;
    let variant: string | null = null;
    try {
      visitorId = mod.getVisitorId();
    } catch {
      /* ignore */
    }
    try {
      variant = mod.getAssignedVariant(AB_EXPERIMENT_KEY);
    } catch {
      /* ignore */
    }
    return { visitorId, variant };
  } catch {
    return { visitorId: null, variant: null };
  }
}

/**
 * Ein Metrik-Wert an die Edge Function schicken. Fire-and-forget, schluckt jeden
 * Fehler. Wird pro (Metrik) höchstens einmal pro Page-Load gefeuert.
 */
async function report(metric: {
  name: string;
  value: number;
  rating?: string;
  navigationType?: string;
  attribution?: unknown;
}): Promise<void> {
  try {
    if (reported.has(metric.name)) return;
    reported.add(metric.name);

    // Kaputte Werte gar nicht erst senden.
    if (typeof metric.value !== "number" || !Number.isFinite(metric.value)) {
      return;
    }

    // Nie einen Wert für eine Seite verschicken, die noch niemand gesehen hat
    // (s. Kopfkommentar). Ohne Prerender löst das sofort auf.
    await bisAktiviert();

    const { visitorId, variant } = await getAbContext();

    const body = {
      metric: metric.name,
      value: metric.value,
      rating: metric.rating ?? null,
      page_path:
        typeof location !== "undefined" ? location.pathname : null,
      landing_path: landingPath,
      attribution: compactAttribution(metric.name, metric.attribution),
      visitor_id: visitorId,
      ab_variant: variant,
      ab_experiment_key: AB_EXPERIMENT_KEY,
      navigation_type: metric.navigationType ?? null,
      // web/ios/android: native App und Website getrennt auswertbar (wie page_views.platform, #746).
      platform: plattform(),
    };

    // Fire-and-forget — Fehler werden geschluckt, blockieren NIE die Seite.
    supabase.functions
      .invoke("track-web-vitals", { body })
      .catch(() => {});
  } catch {
    /* Tracking darf NIE die Seite crashen */
  }
}

/**
 * initWebVitals · registriert alle fünf Core-Web-Vitals-Handler. Nur
 * client-seitig aufrufen. Wirft NIE — bei jedem Fehler still no-op.
 */
export function initWebVitals(): void {
  try {
    if (typeof window === "undefined") return;

    try {
      landingPath = location.pathname;
    } catch {
      /* ignore */
    }

    // Dynamischer Import: fehlt das Paket (Bundle-Fehler), degradiert alles
    // still, statt beim App-Start zu crashen. Attribution-Build: liefert
    // zusätzlich, WELCHES Element die Metrik verursacht (z. B. CLS-Shifter).
    import("web-vitals/attribution")
      .then(({ onLCP, onCLS, onINP, onFCP, onTTFB }) => {
        try {
          onLCP((m) => report(m));
          onCLS((m) => report(m));
          onINP((m) => report(m));
          onFCP((m) => report(m));
          onTTFB((m) => report(m));
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        /* web-vitals nicht ladbar → still ignorieren */
      });
  } catch {
    /* ignore */
  }
}
